import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SalariesService from "services/SalariesService";

const initialState = {
  salaries: [],
  salary: null,
  instructorSalary: null,
  loading: false,
  error: null,
};

// Thunk pour obtenir tous les salaires
export const getSalaries = createAsyncThunk(
  "salaries/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SalariesService.getSalaries();
      return response.data.salaries;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error loading salaries"
      );
    }
  }
);

// Thunk pour obtenir un salaire par ID
export const getSalaryById = createAsyncThunk(
  "salaries/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await SalariesService.getSalaryById(id);
      return response.data.salary;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error loading salary"
      );
    }
  }
);

// Thunk pour obtenir le salaire d'un instructeur spécifique
export const getInstructorSalary = createAsyncThunk(
  "salaries/getInstructorSalary",
  async (instructorId, { rejectWithValue }) => {
    try {
      const response = await SalariesService.getInstructorSalary(instructorId);
      return response.data.salary;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error loading instructor salary"
      );
    }
  }
);

// Thunk pour effectuer un paiement
export const payInstructor = createAsyncThunk(
  "salaries/payInstructor",
  async (data, { rejectWithValue }) => {
    try {
      const response = await SalariesService.payInstructor(data);
      return response.data.salary;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error processing payment"
      );
    }
  }
);

const salariesSlice = createSlice({
  name: "salaries",
  initialState,
  reducers: {
    clearSalaryData: (state) => {
      state.salary = null;
      state.instructorSalary = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Cas pour getSalaries
      .addCase(getSalaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalaries.fulfilled, (state, { payload }) => {
        state.salaries = payload;
        state.loading = false;
      })
      .addCase(getSalaries.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Cas pour getSalaryById
      .addCase(getSalaryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalaryById.fulfilled, (state, { payload }) => {
        state.salary = payload;
        state.loading = false;
      })
      .addCase(getSalaryById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Cas pour getInstructorSalary
      .addCase(getInstructorSalary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInstructorSalary.fulfilled, (state, { payload }) => {
        state.instructorSalary = payload;
        state.loading = false;
      })
      .addCase(getInstructorSalary.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Cas pour payInstructor
      .addCase(payInstructor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(payInstructor.fulfilled, (state, { payload }) => {
        // Mettre à jour le salary correspondant dans la liste si disponible
        if (state.salaries.length > 0) {
          state.salaries = state.salaries.map((salary) =>
            salary._id === payload._id ? payload : salary
          );
        }
        // Mettre à jour le salary actif si c'est celui qui est modifié
        if (state.salary && state.salary._id === payload._id) {
          state.salary = payload;
        }
        // Mettre à jour instructorSalary si c'est le même instructeur
        if (
          state.instructorSalary &&
          state.instructorSalary._id === payload._id
        ) {
          state.instructorSalary = payload;
        }
        state.loading = false;
      })
      .addCase(payInstructor.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });
  },
});

export const { clearSalaryData } = salariesSlice.actions;

export default salariesSlice.reducer;
